/** Keep the browser's one-shot install opportunity across SPA navigation. */
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type InstallSnapshot = {
  prompt: BeforeInstallPromptEvent | null;
  installed: boolean;
  platform: "ios" | "android" | "other";
};
const serverSnapshot: InstallSnapshot = { prompt: null, installed: false, platform: "other" };
let snapshot = serverSnapshot;
let capturedWindow: Window | undefined;
let driverIdentity = false;
const subscribers = new Set<() => void>();

function publish(next: InstallSnapshot) {
  snapshot = next;
  subscribers.forEach((listener) => listener());
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(window.matchMedia?.("(display-mode: standalone)").matches)
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function getInstallPlatform(): "ios" | "android" | "other" {
  if (typeof window === "undefined") return "other";
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  if (/iPhone|iPad|iPod/i.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "other";
}

export function initializePwaInstallCapture() {
  if (typeof window === "undefined" || capturedWindow === window) return;
  capturedWindow = window;
  driverIdentity = isDriverPath(window.location.pathname);
  snapshot = { prompt: null, installed: isStandalone(), platform: getInstallPlatform() };
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    publish({ ...snapshot, prompt: event as BeforeInstallPromptEvent, installed: false });
  });
  window.addEventListener("appinstalled", () => publish({ ...snapshot, prompt: null, installed: true }));
}

export function subscribePwaInstall(listener: () => void) {
  subscribers.add(listener);
  return () => { subscribers.delete(listener); };
}
export const getPwaInstallSnapshot = () => snapshot;
export const getPwaInstallServerSnapshot = () => serverSnapshot;

const isDriverPath = (pathname: string) => pathname === "/driver" || pathname.startsWith("/driver/");
export function syncPwaInstallPath(pathname: string) {
  const next = isDriverPath(pathname);
  if (next === driverIdentity) return;
  driverIdentity = next;
  // Browser prompts belong to the manifest that was active when they fired.
  publish({ ...snapshot, prompt: null, installed: isStandalone() });
}

/** Consume before awaiting: repeated clicks cannot invoke the same event twice. */
export async function promptPwaInstall(): Promise<"accepted" | "dismissed" | "unavailable" | "error"> {
  const event = snapshot.prompt;
  if (!event) return "unavailable";
  publish({ ...snapshot, prompt: null });
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } catch {
    return "error";
  }
}

// Imported by the root module, before account-specific lazy routes mount.
initializePwaInstallCapture();
